<template>
  <div class="qfn-settings">
    <p class="qfn-subtitle">
      Mahalla jamg'armasi bo'yicha qaysi hodisalar va qaysi kanallar orqali
      xabar yuborilishini boshqarish. O'zgarish darhol kuchga kiradi.
    </p>

    <el-alert type="info" show-icon :closable="false" class="qfn-note">
      <template #default>
        Turni o'chirsangiz, yuborilish jarayonida turgan xabarlar ham
        yuborilmaydi — har bir xabar kanalga chiqishdan oldin bu sozlama qayta
        o'qiladi. Fuqarolarning tanlovi saqlanadi — qayta yoqilsa hamma obunachi
        avvalgi holatida qaytadi.
      </template>
    </el-alert>

    <el-skeleton v-if="loading" :rows="8" animated class="qfn-skeleton" />

    <el-result
      v-else-if="errorMessage"
      icon="error"
      title="Sozlamalarni yuklab bo'lmadi"
      :sub-title="errorMessage"
    >
      <template #extra>
        <el-button type="primary" @click="fetchSettings">
          <el-icon style="margin-right: 4px"><Refresh /></el-icon>
          Qayta urinish
        </el-button>
      </template>
    </el-result>

    <MfEmpty
      v-else-if="!settings.length"
      title="Bildirishnoma turlari topilmadi"
      description="Backendda notification_settings jadvali bo'sh bo'lishi mumkin"
    />

    <div v-else class="qfn-cards">
      <div v-for="setting in settings" :key="setting.key" class="qfn-card">
        <div class="qfn-card__head">
          <div class="qfn-card__titles">
            <h3 class="qfn-card__title">{{ title(setting) }}</h3>
            <p v-if="setting.description" class="qfn-card__description">
              {{ setting.description }}
            </p>
            <code class="qfn-card__key">{{ setting.key }}</code>
          </div>
          <el-tag :type="setting.is_enabled ? 'success' : 'info'" effect="light">
            {{ setting.is_enabled ? "Faol" : "O'chiq" }}
          </el-tag>
        </div>

        <div class="qfn-row qfn-row--master">
          <span class="qfn-row__label">Faol</span>
          <el-switch
            :model-value="!!setting.is_enabled"
            :loading="isPending(setting.key, 'is_enabled')"
            :disabled="isPending(setting.key, 'is_enabled')"
            @change="(value) => onToggleType(setting, value)"
          />
        </div>

        <div class="qfn-channels">
          <div v-for="channel in channels" :key="channel.key" class="qfn-channel">
            <div class="qfn-row">
              <span class="qfn-row__label">
                {{ channel.label }} orqali
                <el-icon v-if="channel.key === 'sms'" class="qfn-row__warning">
                  <WarningFilled />
                </el-icon>
              </span>
              <el-switch
                :model-value="!!setting[channel.flag]"
                :loading="isPending(setting.key, channel.flag)"
                :disabled="!setting.is_enabled || isPending(setting.key, channel.flag)"
                @change="(value) => onToggleChannel(setting, channel, value)"
              />
            </div>
            <p v-if="channel.key === 'sms'" class="qfn-channel__warning">
              SMS eng qimmat kanal. ~10 000 mahalla bo'ylab yoqilganda xarajat
              sezilarli oshadi.
            </p>
          </div>
        </div>

        <p v-if="!setting.is_enabled" class="qfn-card__hint">
          Tur o'chirilgan — kanal sozlamalari saqlanadi, lekin ishlamaydi.
        </p>

        <div class="qfn-card__footer">
          Oxirgi o'zgarish: {{ formatDate(setting.updated_at) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { Refresh, WarningFilled } from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import api, {
  CHANNELS,
  apiErrorMessage,
  settingTitle,
} from "./quarterFundNotificationsApi";

export default {
  name: "NotificationSettings",
  components: { MfEmpty, Refresh, WarningFilled },
  data() {
    return {
      loading: true,
      errorMessage: "",
      settings: [],
      // `${key}|${flag}` -> true, har bir switch alohida loading holatida
      pending: {},
      channels: CHANNELS,
    };
  },
  mounted() {
    this.$store.commit("updateHeaderTitle", "Bildirishnoma turlari");
    this.fetchSettings();
  },
  methods: {
    title(setting) {
      return settingTitle(setting);
    },
    formatDate(value) {
      if (!value) return "—";
      return this.$moment(value).format("DD.MM.YYYY HH:mm");
    },
    isPending(key, flag) {
      return !!this.pending[`${key}|${flag}`];
    },
    fetchSettings() {
      this.loading = true;
      this.errorMessage = "";
      return api
        .settings()
        .then(({ data }) => {
          this.settings = data?.settings || [];
        })
        .catch(async (error) => {
          this.errorMessage = await apiErrorMessage(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Turni o'chirish barcha obunachilarga ta'sir qiladi — tasdiq so'raladi.
    // model-value controlled bo'lgani uchun bekor qilinsa switch o'z holida qoladi.
    onToggleType(setting, value) {
      if (value) {
        this.patch(setting, "is_enabled", true);
        return;
      }
      ElMessageBox.confirm(
        "Bu bildirishnoma turi barcha obunachilar uchun to'xtatiladi. Ularning sozlamalari saqlanadi.",
        `"${this.title(setting)}" turini o'chirish`,
        {
          type: "warning",
          confirmButtonText: "O'chirish",
          cancelButtonText: "Bekor qilish",
          confirmButtonClass: "el-button--danger",
        }
      )
        .then(() => this.patch(setting, "is_enabled", false))
        .catch(() => {
          // bekor qilindi
        });
    },
    // SMS eng qimmat kanal — yoqishdan oldin tasdiq so'raladi.
    onToggleChannel(setting, channel, value) {
      if (channel.key !== "sms" || !value) {
        this.patch(setting, channel.flag, value);
        return;
      }
      ElMessageBox.confirm(
        "SMS eng qimmat kanal. ~10 000 mahalla bo'ylab yoqilganda xarajat sezilarli oshadi.",
        `"${this.title(setting)}" uchun SMS ni yoqish`,
        {
          type: "warning",
          confirmButtonText: "Yoqish",
          cancelButtonText: "Bekor qilish",
        }
      )
        .then(() => this.patch(setting, channel.flag, true))
        .catch(() => {
          // bekor qilindi
        });
    },
    // Qismli PATCH: faqat bitta flag ketadi. Optimistik yangilash, xatoda rollback.
    patch(setting, flag, value) {
      const pendingKey = `${setting.key}|${flag}`;
      const previous = setting[flag];
      this.pending = { ...this.pending, [pendingKey]: true };
      setting[flag] = value;

      api
        .updateSetting(setting.key, { [flag]: value })
        .then(({ data }) => {
          // Server javobi source of truth — parallel o'zgarish bo'lsa ham sinxron qoladi.
          const updated = data?.key ? data : data?.setting;
          if (updated?.key) {
            const index = this.settings.findIndex((item) => item.key === updated.key);
            if (index !== -1) this.settings.splice(index, 1, updated);
          }
          ElMessage.success("Saqlandi");
        })
        .catch(async (error) => {
          setting[flag] = previous;
          if (error?.response?.status === 404) {
            ElNotification.error({
              title: "Tur topilmadi",
              message: "Bu bildirishnoma turi backendda mavjud emas, ro'yxat yangilanadi.",
            });
            this.fetchSettings();
            return;
          }
          ElNotification.error({
            title: "Saqlanmadi",
            message: await apiErrorMessage(error, "O'zgarishni saqlab bo'lmadi"),
          });
        })
        .finally(() => {
          const next = { ...this.pending };
          delete next[pendingKey];
          this.pending = next;
        });
    },
  },
};
</script>

<style scoped>
.qfn-settings {
  padding: 4px 0;
}
.qfn-subtitle {
  margin: 0;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.5;
  color: #909399;
}
.qfn-note {
  margin: 16px 0;
}
.qfn-skeleton {
  margin-top: 16px;
}
.qfn-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
}
.qfn-card {
  padding: 18px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}
.qfn-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.qfn-card__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.qfn-card__description {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
}
.qfn-card__key {
  display: inline-block;
  margin-top: 8px;
  font-size: 11px;
  color: #a8abb2;
}
.qfn-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  font-size: 13px;
  color: #606266;
}
.qfn-row--master {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f2f6fc;
  font-weight: 600;
  color: #303133;
}
.qfn-row__label {
  display: flex;
  align-items: center;
  gap: 6px;
}
.qfn-row__warning {
  color: #e6a23c;
}
.qfn-channels {
  padding-left: 14px;
  border-left: 2px solid #f2f6fc;
}
.qfn-channel__warning {
  margin: -4px 0 6px;
  font-size: 11px;
  line-height: 1.45;
  color: #e6a23c;
}
.qfn-card__hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #c0c4cc;
}
.qfn-card__footer {
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #f2f6fc;
  font-size: 12px;
  color: #909399;
}

@media (max-width: 768px) {
  .qfn-cards {
    grid-template-columns: 1fr;
  }
}
</style>
